// client/src/components/anamnese/AnamneseForm.js
// Fluxo: escolhe cliente+template(+serviço) → cria rascunho (dá um _id real
// pra anexar fotos) → preenche via AnamneseFillForm → finaliza assinando na
// hora ou gerando um link de assinatura remota (cliente assina depois, sem
// login, via publicAnamneseController/AssinarAnamnesePublica).
import React, { useMemo, useRef, useState } from 'react';
import { Save, XCircle, Loader2, PenLine, Link2, Copy, Check, ArrowRight } from 'lucide-react';
import { useMyAccountDetails } from '../../hooks/useAccounts';
import { useClientes } from '../../hooks/useClientes';
import { useTemplatesAnamnese } from '../../hooks/useTemplateAnamnese';
import { useServicos } from '../../hooks/useServicos';
import { useCreateAnamneseDraft, useUpdateAnamnese, useGerarLinkAssinatura } from '../../hooks/useAnamneses';
import AnamneseFillForm, { TIPOS_COM_RESPOSTA } from './AnamneseFillForm';
import AssinaturaCanvas from '../common/AssinaturaCanvas';
import useAlert from '../../hooks/useAlert';

const getClienteNome = (c) => c?.user?.name || c?.nome || 'Cliente sem nome';

const buildRespostasPayload = (template, respostas) => {
    if (!template) return [];
    return (template.conteudo || [])
        .filter((item) => TIPOS_COM_RESPOSTA.has(item.type) && item.type !== 'Galeria')
        .map((item) => ({ itemId: item.id, valor: respostas[item.id] }))
        .filter((r) => r.valor !== undefined && r.valor !== '' && !(Array.isArray(r.valor) && r.valor.length === 0));
};

// Reconstrói o dicionário local {itemId: valor} a partir de RespostaAnamneseSalva[]
// (formato salvo no banco) pra retomar um rascunho já iniciado — mesma
// conversão booleana usada em AnamneseFillForm ('Verdadeiro ou Falso' vive
// como true/false no estado local, mas pode ter sido salvo como 'Sim'/'Não').
const buildRespostasIniciais = (fichaExistente) => {
    const respostas = {};
    (fichaExistente?.respostas || []).forEach((r) => {
        respostas[r.itemId] = r.tipo === 'Verdadeiro ou Falso' ? (r.valor === true || r.valor === 'Sim') : r.valor;
    });
    return respostas;
};

// fichaExistente: ficha em 'draft' (com respostas parciais ou vazias) que o
// usuário está retomando a partir do ActionsDrawer ("Continuar Preenchimento")
// — pula a etapa 1 (seleção de cliente/template) e já entra preenchendo.
const AnamneseForm = ({ onCloseForm, onSaveSuccess, fichaExistente = null }) => {
    const { setAlert } = useAlert();
    const { data: accountData } = useMyAccountDetails();
    const accountId = accountData?._id;

    const { data: clientesData, isLoading: isLoadingClientes } = useClientes({ limit: 1000, populate: 'user' }, accountId);
    const { data: templates, isLoading: isLoadingTemplates } = useTemplatesAnamnese();
    const { data: servicosData } = useServicos({ limit: 200 });

    const clientes = clientesData?.data || [];
    const servicos = servicosData?.data || [];

    const createDraft = useCreateAnamneseDraft();
    const updateAnamnese = useUpdateAnamnese();
    const gerarLink = useGerarLinkAssinatura();
    const canvasRef = useRef(null);

    const [clienteId, setClienteId] = useState('');
    const [templateId, setTemplateId] = useState(fichaExistente?.template || '');
    const [servicoId, setServicoId] = useState('');
    const [anamnese, setAnamnese] = useState(fichaExistente);
    const [respostas, setRespostas] = useState(() => buildRespostasIniciais(fichaExistente));
    const [modoAssinatura, setModoAssinatura] = useState(false);
    const [linkGerado, setLinkGerado] = useState(null);
    const [linkCopiado, setLinkCopiado] = useState(false);

    const template = useMemo(() => (templates || []).find((t) => t._id === templateId) || null, [templates, templateId]);

    const onChangeResposta = (itemId, valor) => {
        setRespostas((prev) => ({ ...prev, [itemId]: valor }));
    };

    const handleIniciarPreenchimento = (e) => {
        e.preventDefault();
        if (!clienteId || !templateId) {
            setAlert('Selecione o cliente e o template de anamnese.', 'warning');
            return;
        }
        createDraft.mutate(
            { clienteId, templateId, servicoId: servicoId || undefined },
            { onSuccess: (data) => setAnamnese(data) }
        );
    };

    const handleSalvarRascunho = () => {
        updateAnamnese.mutate({ id: anamnese._id, respostas: buildRespostasPayload(template, respostas) });
    };

    const handleAssinarAgora = () => {
        const assinatura = canvasRef.current?.getSignature();
        if (!assinatura) {
            setAlert('Colete a assinatura no campo indicado antes de confirmar.', 'warning');
            return;
        }
        updateAnamnese.mutate(
            { id: anamnese._id, respostas: buildRespostasPayload(template, respostas), assinaturaCliente: assinatura },
            {
                onSuccess: () => {
                    setAlert('Ficha assinada com sucesso!', 'success');
                    onSaveSuccess?.();
                },
            }
        );
    };

    const handleGerarLink = () => {
        updateAnamnese.mutate(
            { id: anamnese._id, respostas: buildRespostasPayload(template, respostas), gerarLinkAssinatura: true },
            {
                onSuccess: () => {
                    gerarLink.mutate(anamnese._id, {
                        onSuccess: (data) => setLinkGerado(data.linkAssinatura),
                    });
                },
            }
        );
    };

    const handleFinalizarSemAssinatura = () => {
        updateAnamnese.mutate(
            { id: anamnese._id, respostas: buildRespostasPayload(template, respostas), finalizar: true },
            {
                onSuccess: () => {
                    setAlert('Ficha finalizada com sucesso!', 'success');
                    onSaveSuccess?.();
                },
            }
        );
    };

    const handleCopiarLink = () => {
        navigator.clipboard.writeText(linkGerado);
        setLinkCopiado(true);
        setTimeout(() => setLinkCopiado(false), 2000);
    };

    // Etapa 1 — seleção de cliente/template/serviço, ainda sem rascunho criado
    if (!anamnese) {
        return (
            <form onSubmit={handleIniciarPreenchimento}>
                <div className="form-section">
                    <h4 className="form-section-title">Nova Ficha de Anamnese</h4>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label className="form-label">Cliente*</label>
                            <select className="form-input" value={clienteId} onChange={(e) => setClienteId(e.target.value)} disabled={isLoadingClientes}>
                                <option value="">Selecione o cliente</option>
                                {clientes.map((c) => (
                                    <option key={c._id} value={c._id}>{getClienteNome(c)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Template*</label>
                            <select className="form-input" value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={isLoadingTemplates}>
                                <option value="">Selecione o template</option>
                                {(templates || []).map((t) => (
                                    <option key={t._id} value={t._id}>{t.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Serviço vinculado (opcional)</label>
                            <select className="form-input" value={servicoId} onChange={(e) => setServicoId(e.target.value)}>
                                <option value="">Nenhum — ficha geral do cliente</option>
                                {servicos.map((s) => (
                                    <option key={s._id} value={s._id}>{s.nome}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">
                        <XCircle size={18} className="icon" /> Cancelar
                    </button>
                    <button type="submit" disabled={createDraft.isPending} className="action-button action-button-primary">
                        {createDraft.isPending ? <Loader2 size={18} className="animate-spin icon" /> : <ArrowRight size={18} className="icon" />}
                        Iniciar Preenchimento
                    </button>
                </div>
            </form>
        );
    }

    // Etapa 3 — link de assinatura remota já gerado, só falta copiar/enviar
    if (linkGerado) {
        return (
            <div className="form-section">
                <h4 className="form-section-title">Link de Assinatura Gerado</h4>
                <p className="text-muted">
                    Envie o link abaixo para o cliente assinar remotamente. Ele vai precisar confirmar um código de verificação antes de ver as respostas e assinar. O link expira em 7 dias.
                </p>
                <div className="form-group full-width">
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input className="form-input" readOnly value={linkGerado} onFocus={(e) => e.target.select()} />
                        <button type="button" onClick={handleCopiarLink} className="action-button action-button-secondary" style={{ whiteSpace: 'nowrap' }}>
                            {linkCopiado ? <Check size={18} className="icon" /> : <Copy size={18} className="icon" />}
                            {linkCopiado ? 'Copiado!' : 'Copiar'}
                        </button>
                    </div>
                </div>
                <div className="form-actions">
                    <button type="button" onClick={() => { onSaveSuccess?.(); }} className="action-button action-button-primary">
                        Concluir
                    </button>
                </div>
            </div>
        );
    }

    // Etapa 2b — coletando a assinatura na hora
    if (modoAssinatura) {
        return (
            <div className="form-section">
                <h4 className="form-section-title">Assinatura do Cliente</h4>
                <div className="form-group full-width">
                    <AssinaturaCanvas ref={canvasRef} />
                </div>
                <div className="form-actions">
                    <button type="button" onClick={() => setModoAssinatura(false)} className="action-button action-button-secondary">
                        <XCircle size={18} className="icon" /> Voltar
                    </button>
                    <button type="button" onClick={handleAssinarAgora} disabled={updateAnamnese.isPending} className="action-button action-button-primary">
                        {updateAnamnese.isPending ? <Loader2 size={18} className="animate-spin icon" /> : <Save size={18} className="icon" />}
                        Confirmar Assinatura
                    </button>
                </div>
            </div>
        );
    }

    // Etapa 2 — preenchimento das respostas do template
    return (
        <div>
            <AnamneseFillForm
                template={template}
                respostas={respostas}
                onChangeResposta={onChangeResposta}
                anamneseId={anamnese._id}
            />

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">
                    <XCircle size={18} className="icon" /> Cancelar
                </button>
                <button type="button" onClick={handleSalvarRascunho} disabled={updateAnamnese.isPending} className="action-button action-button-secondary">
                    {updateAnamnese.isPending ? <Loader2 size={18} className="animate-spin icon" /> : <Save size={18} className="icon" />}
                    Salvar Rascunho
                </button>
                {template?.requerAssinatura ? (
                    <>
                        <button type="button" onClick={handleGerarLink} disabled={updateAnamnese.isPending || gerarLink.isPending} className="action-button action-button-secondary">
                            {(updateAnamnese.isPending || gerarLink.isPending) ? <Loader2 size={18} className="animate-spin icon" /> : <Link2 size={18} className="icon" />}
                            Gerar Link de Assinatura
                        </button>
                        <button type="button" onClick={() => setModoAssinatura(true)} className="action-button action-button-primary">
                            <PenLine size={18} className="icon" /> Assinar Agora
                        </button>
                    </>
                ) : (
                    <button type="button" onClick={handleFinalizarSemAssinatura} disabled={updateAnamnese.isPending} className="action-button action-button-primary">
                        {updateAnamnese.isPending ? <Loader2 size={18} className="animate-spin icon" /> : <Save size={18} className="icon" />}
                        Finalizar Ficha
                    </button>
                )}
            </div>
        </div>
    );
};

export default AnamneseForm;
